/**
 * ✅ FIXED VERSION - Preserves any country code and last 4 digits
 * 
 * Mask a phone number: KEEP country code, HIDE middle, SHOW last 4
 * Supports +1, +44, +91, +971, etc.
 * 
 * Examples:
 *  919876543210 → 91XXXXX3210
 *  +919876543210 → +91XXXXX3210
 *  +447400123456 → +44XXXXXX3456
 *  +971501234567 → +971XXXXX4567
 * 
 * @param {string|number} phoneNumber - The phone number to mask
 * @returns {string} - Masked phone number
 */
/**
 * ✅ Robust Country Code Extraction
 * Supports 1, 2, and 3-digit international codes.
 */
const identifyCountryCode = (phoneStr) => {
  if (!phoneStr) return { code: '', national: '' };

  const clean = phoneStr.replace(/[^\d+]/g, '');
  let hasPlus = clean.startsWith('+');
  let digits = hasPlus ? clean.substring(1) : clean;
  let prefix = hasPlus ? '+' : '';

  // 1. Handle common 3-digit codes (Bangaldesh, UAE, etc.)
  const threeDigitCodes = [
    '880', '852', '886', '971', '972', '973', '974', '975', '976',
    '254', '255', '256', '211', '212', '213', '216', '218', '220'
  ];
  if (digits.length >= 7) {
    const code = digits.substring(0, 3);
    if (threeDigitCodes.includes(code)) {
      return { code: prefix + code, national: digits.substring(3) };
    }
  }

  // 2. Handle common 2-digit codes (India, UK, etc.)
  const twoDigitCodes = [
    '91', '44', '49', '33', '34', '39', '41', '43', '61', '64', '81', '82', '86', '90', '92', '93', '94', '95', '98',
    '20', '27', '30', '31', '32', '36', '40', '45', '46', '47', '48', '51', '52', '53', '54', '55', '56', '57', '58',
    '60', '62', '63', '65', '66'
  ];
  if (digits.length >= 7 || (digits.startsWith('91') && digits.length >= 12)) {
    const code = digits.substring(0, 2);
    if (twoDigitCodes.includes(code)) {
      return { code: prefix + code, national: digits.substring(2) };
    }
  }

  // 3. Handle 1-digit codes (US, Russia)
  const oneDigitCodes = ['1', '7'];
  if (digits.length >= 7) {
    const code = digits.substring(0, 1);
    if (oneDigitCodes.includes(code)) {
      return { code: prefix + code, national: digits.substring(1) };
    }
  }

  // 4. Heuristic: If it has +, assume anything before the last 10 digits is code
  if (hasPlus && digits.length > 10) {
    return {
      code: prefix + digits.substring(0, digits.length - 10),
      national: digits.substring(digits.length - 10)
    };
  }

  return { code: '', national: digits };
};

export { identifyCountryCode };

export const MOBILE_MASKING_STORAGE_KEY = 'agent_portal_mobile_masking';

export const isGlobalPhoneMaskingEnabled = () => {
  const raw = window.localStorage.getItem(MOBILE_MASKING_STORAGE_KEY);
  return raw === 'true';
};

export const setGlobalPhoneMaskingEnabled = (enabled) => {
  window.localStorage.setItem(MOBILE_MASKING_STORAGE_KEY, String(enabled));
};

export const maskPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return 'Unknown';

  const raw = String(phoneNumber).trim();
  const digits = raw.replace(/\D/g, '');
  if (!digits) return raw || 'Unknown';

  const hasPlus = raw.startsWith('+');
  const visibleDigits = digits.slice(-4);
  const maskedLength = Math.max(digits.length - 4, 0);
  const masked = `${'*'.repeat(maskedLength)}${visibleDigits}`;

  return hasPlus ? `+${masked}` : masked;
};

/**
 * Get display number based on the current global masking toggle.
 * If a specific explicit shouldMask flag is provided, it overrides the global setting.
 */
export const getDisplayPhoneNumber = (phoneNumber, shouldMask = isGlobalPhoneMaskingEnabled()) => {
  if (!phoneNumber) return 'Unknown';

  const raw = String(phoneNumber).trim();
  const clean = raw.replace(/[^\d+]/g, '');

  if (!clean) return 'Unknown';
  if (!shouldMask) return clean;

  return maskPhoneNumber(raw);
};

export const getDisplayTextWithMasking = (text, shouldMask = false) => {
  if (!text || !shouldMask) return text;

  const stringText = String(text);
  const maskedText = stringText.replace(/(\+?\d[\d\s().-]{5,}\d)/g, (match) => {
    const digits = match.replace(/\D/g, '');
    if (!digits || digits.length <= 4) return match;
    const visible = digits.slice(-4);
    const prefix = match.startsWith('+') ? '+' : '';
    const starCount = Math.max(digits.length - 4, 0);
    return `${prefix}${'*'.repeat(starCount)}${visible}`;
  });

  return maskedText;
};